// eslint-disable-next-line no-restricted-imports
import {
	isCustomApiError,
	type PostRegistrationUserValidationEmailCodeData,
	type PostRegistrationUserValidationEmailCodePayload,
	type PostRegistrationUserValidationMobileNumberCodeData,
	type PostRegistrationUserValidationMobileNumberCodePayload,
	safeRequest,
	useIntl
} from '@capsicum/common';
import { useCallback } from 'react';
import { match } from 'ts-pattern';

import { postRegistrationValidationEmailCode, postRegistrationValidationMobileNumberCode } from '../data-access';

export const useRegistrationVerification = () => {
	const { $t } = useIntl();

	const registrationVerificationSMS = useCallback(
		async (
			validationParams: PostRegistrationUserValidationMobileNumberCodePayload
		): Promise<{
			error?: { field: string; message?: string; type: string };
			formattedValues?: Exclude<PostRegistrationUserValidationMobileNumberCodeData['data'], undefined>;
		}> => {
			const { data, error, success } = await safeRequest(() =>
				postRegistrationValidationMobileNumberCode(validationParams)
			);

			if (success) {
				const err = match<
					Exclude<PostRegistrationUserValidationMobileNumberCodeData['data'], undefined>,
					| {
							field: string;
							message?: string;
							type: string;
					  }
					| undefined
				>(data)
					.with({ isValid: false }, () => ({
						field: 'invalidCode',
						message: $t('auth.failedCodeVerification'),
						type: 'api'
					}))
					.otherwise(() => undefined);

				return {
					error: err,
					formattedValues: {
						isValid: data.isValid
					}
				};
			} else {
				const errorMessage = isCustomApiError(error) ? error.errors?.[0]?.title : $t('global.error');

				return {
					error: {
						field: 'mobileNumber',
						/**
						 * @todo - Create a util to properly extract the errors
						 */
						message: errorMessage,
						type: 'api'
					}
				};
			}
		},
		[$t]
	);

	const registrationVerificationEmail = useCallback(
		async (
			validationParams: PostRegistrationUserValidationEmailCodePayload
		): Promise<{
			error?: { field: string; message?: string; type: string };
			formattedValues?: Exclude<PostRegistrationUserValidationEmailCodeData['data'], undefined>;
		}> => {
			const { data, error, success } = await safeRequest(() =>
				postRegistrationValidationEmailCode(validationParams)
			);

			if (success) {
				const err = match<
					Exclude<PostRegistrationUserValidationEmailCodeData['data'], undefined>,
					| {
							field: string;
							message?: string;
							type: string;
					  }
					| undefined
				>(data)
					.with({ isValid: false }, () => ({
						field: 'invalidCode',
						message: $t('auth.failedCodeVerification'),
						type: 'api'
					}))
					.otherwise(() => undefined);

				return {
					error: err,
					formattedValues: {
						isValid: data.isValid
					}
				};
			} else {
				const errorMessage = isCustomApiError(error) ? error.errors?.[0]?.title : $t('global.error');

				return {
					error: {
						field: 'email',
						/**
						 * @todo - Create a util to properly extract the errors
						 */
						message: errorMessage,
						type: 'api'
					}
				};
			}
		},
		[$t]
	);

	return { registrationVerificationEmail, registrationVerificationSMS };
};
